import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { makeStyles } from "@mui/styles";
import "flatpickr/dist/themes/material_blue.css";
import { useEffect, useState } from "react";
import Flatpickr from "react-flatpickr";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { InputGroup } from "reactstrap";
import docImg from "../../../assets/icons/document.png";
import { config } from "../../../config/index";
import { GET, newFetch, POST } from "../../../helpers/fetch";
import Upload from "../../Input/Upload";
import ToastUtil from "../../ToastNotification/ToastUtil";
const { SERVER_URL } = config;

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const useStyles = makeStyles({
  root: {
    minWidth: 275,
  },
});

const LeaveForm = (props) => {
  const { t } = useTranslation();
  const [remarks, setRemarks] = useState("");
  const [dayType, setDayType] = useState(0);
  const [selectedLeaveType, setSelectedLeaveType] = useState("");
  const [files, setFiles] = useState([]);
  const [leaveDropdown, setLeaveDropdown] = useState([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showDayType, setShowDayType] = useState(false);

  // changed leavePeriod -> leaveFrom & leaveTo
  const [leaveFrom, setLeaveFrom] = useState(null);
  const [leaveTo, setLeaveTo] = useState(null);

  const history = useHistory();
  const associateId = JSON.parse(localStorage.getItem("user")).id;

  useEffect(() => {
    const fetchLeaveDropdown = async () => {
      try {
        const apiPath = `/associate-leave-summary/dropdown/${associateId}`;
        const response = await newFetch(GET, apiPath);

        if (response.success) {
          setLeaveDropdown(response.data);
        } else {
          ToastUtil.error(response.message || "Failed to load leave types");
        }
      } catch (err) {
        console.error("Dropdown fetch error:", err);
        ToastUtil.error("Unable to fetch leave types");
      }
    };

    !props.leaveData && fetchLeaveDropdown();
  }, [associateId]);

  const getLeaveSummaryByAssociateId = async () => {
    try {
      const apiPath = `/associate-summary/${associateId}`;
      const response = await newFetch(GET, apiPath);

      if (response.success !== false) {
        return response;
      } else {
        ToastUtil.error("Failed to retrieve leave summary.");
        return null;
      }
    } catch (error) {
      ToastUtil.error("Error fetching leave summary.");
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!selectedLeaveType) {
      ToastUtil.error("Select leave type");
      return;
    }

    if (!leaveFrom || !leaveTo) {
      ToastUtil.error("Select leave from and leave to dates");
      return;
    }

    if (new Date(leaveFrom) > new Date(leaveTo)) {
      ToastUtil.error("Leave From cannot be later than Leave To");
      return;
    }

    const selectedLeave = leaveDropdown.find(
      (item) => item.value === selectedLeaveType,
    );

    if (selectedLeave?.allow_document && files.length === 0) {
      ToastUtil.error("Upload the document");
      return;
    }

    const summaryList = await getLeaveSummaryByAssociateId();
    if (!summaryList) return;

    const matchedSummary = summaryList?.leave_summary.find(
      (summary) => summary.leave_type_id === parseInt(selectedLeaveType),
    );

    const leaveRestDays = summaryList?.leave_rest_days || 0;
    if (!matchedSummary) {
      ToastUtil.error("No leave summary found for selected type.");
      return;
    }

    // --- Determine fraction for total_days based on dayType ---
    let dayFraction = 0;
    if (selectedLeave.allow_half_day === false || dayType === 0) {
      dayFraction = 1; // Full day
    } else if (dayType === 1 || dayType === 2) {
      dayFraction = 0.5; // Half day AM/PM
    } else {
      ToastUtil.error("Invalid day type");
      return;
    }

    // --- Calculate totalDaysRequested ---
    const startDate = new Date(leaveFrom);
    const endDate = new Date(leaveTo);
    const daysDiff =
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
      ) + 1;

    let weekEndCount = 0;
    for (
      let assignDate = new Date(startDate);
      assignDate <= endDate;
      assignDate.setDate(assignDate.getDate() + 1)
    ) {
      const assignDay = assignDate.getDay();

      if (assignDay === 6 || assignDay === 0) weekEndCount++;
    }

    let totalDaysRequested = daysDiff * dayFraction;
    totalDaysRequested = totalDaysRequested - weekEndCount;

    // --- Check available balance ---
    const availableBalance =
      parseFloat(matchedSummary.available) -
      parseFloat(matchedSummary.pending || 0);

    if (availableBalance < totalDaysRequested) {
      ToastUtil.error(
        `Not enough leave balance. Available: ${availableBalance} days, Requested: ${totalDaysRequested} days`,
      );
      return;
    }

    if (totalDaysRequested <= 0) {
      ToastUtil.error("Invalid leave duration calculated");
      return;
    }

    // --- Prepare FormData ---
    const formData = new FormData();
    formData.append("associate_id", associateId);
    formData.append("leave_type_id", parseInt(selectedLeaveType));
    formData.append("start_date", formatLocalDate(startDate));
    formData.append("end_date", formatLocalDate(endDate));
    formData.append("day_type", dayType); // store 0/1/2 for backend leave_coverage
    formData.append("total_days", totalDaysRequested);
    formData.append("remarks", remarks);
    formData.append("allow_document", selectedLeave?.allow_document);

    if (selectedLeave?.allow_document && files[0]) {
      formData.append("file", files[0]);
    }

    // --- Submit to backend ---
    const apiPath = `/associate-leaves/apply-leave`;
    const response = await newFetch(POST, apiPath, formData, "multi");

    if (response.success) {
      ToastUtil.success(
        response.message || "Leave application submitted successfully!",
      );
      setSelectedLeaveType("");
      setLeaveFrom(null);
      setLeaveTo(null);
      setDayType(0); // reset to full day
      setFiles([]);
      setRemarks("");
      props.updateState?.();
      history.push("/leave");
    } else {
      ToastUtil.error(response.message || "Failed to submit leave application");
    }
  };

  const handleLeaveTypeChange = (e) => {
    const value = e.target.value;
    setSelectedLeaveType(value);

    const selectedLeave = leaveDropdown.find((item) => item.value === value);

    if (!selectedLeave) {
      setShowFileUpload(false);
      setShowDayType(false);
      return;
    }

    setShowFileUpload(selectedLeave.allow_document === true);
    setShowDayType(selectedLeave.allow_half_day === true);
    setDayType(0);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return isNaN(date)
      ? "-"
      : date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  return (
    <Card>
      <CardContent>
        <div className="edit-leave-wrapper">
          {props.leaveData ? (
            <>
              <div className="form-group">
                <div className="header-thick">{t("Type of Leave")}</div>
                <div className="edit-leave-values">
                  {props.leaveData.leave_type_name || "-"}
                </div>
              </div>

              <div className="form-group">
                <div className="header-thick">{t("Leave Coverage")}</div>
                <div className="edit-leave-values">
                  {props.leaveData.leave_coverage === 0
                    ? t("Full Day")
                    : props.leaveData.leave_coverage === 1
                      ? t("Half Day")
                      : props.leaveData.leave_coverage === 2
                        ? t("Half Day")
                        : "-"}
                </div>
              </div>

              <div className="form-group">
                <div className="header-thick">{t("Leave From")}</div>
                <div className="edit-leave-values">
                  {formatDate(props.leaveData.leave_from)}
                </div>
              </div>

              <div className="form-group">
                <div className="header-thick">{t("Leave To")}</div>
                <div className="edit-leave-values">
                  {formatDate(props.leaveData.leave_to)}
                </div>
              </div>

              {props.leaveData?.documents?.length > 0 && (
                <div className="form-group">
                  <label className="header-thick">{t("Attachment")}</label>
                  <br />
                  {props.leaveData.documents.map((doc, index) => (
                    <div key={index} className="mb-2 d-flex align-items-center">
                      <a
                        href={`${SERVER_URL}/storage/leaves/${doc.new_file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex align-items-center"
                      >
                        <img
                          src={docImg}
                          alt="Document Icon"
                          className="me-2"
                          style={{ width: "32px", height: "32px" }}
                        />
                      </a>
                    </div>
                  ))}
                </div>
              )}

              <div className="form-group">
                <div className="header-thick">{t("Remarks")}</div>
                <div className="edit-leave-values">
                  {props.leaveData.remarks || "-"}
                </div>
              </div>

              <div className="form-group">
                <div className="header-thick">{t("Admin Remarks")}</div>
                <div className="edit-leave-values">
                  {props.leaveData.actions && props.leaveData.actions.length > 0
                    ? (() => {
                        // Find the highest pending approver number to determine the last approver
                        const highestPendingNum = Math.max(
                          ...props.leaveData.actions
                            .filter((action) =>
                              action.status?.toLowerCase().includes("pending"),
                            )
                            .map((action) => {
                              const match =
                                action.status.match(/(\d+)(st|nd|rd|th)/i);
                              return match ? parseInt(match[1], 10) : 0;
                            }),
                        );

                        return props.leaveData.actions
                          .filter((action) => action.remarks?.trim())
                          .sort((a, b) => {
                            const getApproverNumber = (str) => {
                              const match = str.match(/(\d+)(st|nd|rd|th)/i);
                              return match ? parseInt(match[1], 10) : 999;
                            };
                            return (
                              getApproverNumber(a.status) -
                              getApproverNumber(b.status)
                            );
                          })
                          .map((action, index) => {
                            let displayLabel;

                            // Check if status is "Approved"
                            if (action.status?.toLowerCase() === "approved") {
                              // Convert number to ordinal (1st, 2nd, 3rd, etc.)
                              const getOrdinal = (n) => {
                                const s = ["th", "st", "nd", "rd"];
                                const v = n % 100;
                                return n + (s[(v - 20) % 10] || s[v] || s[0]);
                              };

                              displayLabel = `${getOrdinal(
                                highestPendingNum,
                              )} Approval`;
                            }
                            // Check if status is "Pending for X Approval"
                            else if (
                              action.status?.toLowerCase().includes("pending")
                            ) {
                              const match =
                                action.status.match(/(\d+)(st|nd|rd|th)/i);
                              if (match) {
                                const pendingApproverNum = parseInt(
                                  match[1],
                                  10,
                                );
                                const previousApproverNum =
                                  pendingApproverNum - 1;

                                const getOrdinal = (n) => {
                                  const s = ["th", "st", "nd", "rd"];
                                  const v = n % 100;
                                  return n + (s[(v - 20) % 10] || s[v] || s[0]);
                                };

                                displayLabel = `${getOrdinal(
                                  previousApproverNum,
                                )} Approval`;
                              } else {
                                displayLabel = action.status;
                              }
                            } else {
                              displayLabel = action.status;
                            }

                            return (
                              <span key={index}>
                                {displayLabel}: {action.remarks} <br />
                              </span>
                            );
                          });
                      })()
                    : "-"}
                </div>
              </div>

              {props.leaveData.actions?.some(
                (action) => action.status === "Rejected",
              ) && (
                <div className="form-group">
                  <div className="font-red">{t("Reason for Rejection")}</div>
                  <div className="edit-leave-values">
                    {props.leaveData.actions.find(
                      (action) => action.status === "Rejected",
                    )?.remarks || "-"}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="form-group">
                <div className="header-thick">{t("Type of Leave")}</div>
                <select
                  value={selectedLeaveType}
                  onChange={handleLeaveTypeChange}
                  className="form-select"
                >
                  <option value="">{t("Select leave type")}</option>
                  {leaveDropdown.map((item) => (
                    <option key={item.value} value={item.value}>
                      {t(item.label)} ({item.remaining})
                    </option>
                  ))}
                </select>
              </div>

              {showDayType && (
                <div className="form-group">
                  <div className="header-thick">{t("Day Type")}</div>
                  <select
                    name="dayType"
                    value={dayType}
                    className="form-select edit-leave-values"
                    onChange={(e) => setDayType(parseFloat(e.target.value))}
                  >
                    <option value="0">{t("Full Day")}</option>
                    <option value="1">{t("Half Day (AM)")}</option>
                    <option value="2">{t("Half Day (PM)")}</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <div className="header-thick">{t("Leave From")}</div>
                <InputGroup>
                  <Flatpickr
                    className="form-control d-block"
                    placeholder="dd/mm/yyyy"
                    value={leaveFrom}
                    onChange={([date]) => setLeaveFrom(date)}
                    options={{
                      dateFormat: "d/m/Y",
                    }}
                  />
                </InputGroup>
              </div>

              <div className="form-group">
                <div className="header-thick">{t("Leave To")}</div>
                <InputGroup>
                  <Flatpickr
                    className="form-control d-block"
                    placeholder="dd/mm/yyyy"
                    value={leaveTo}
                    onChange={([date]) => setLeaveTo(date)}
                    options={{
                      dateFormat: "d/m/Y",
                      minDate: leaveFrom,
                    }}
                  />
                </InputGroup>
              </div>

              {showFileUpload && (
                <div className="form-group">
                  <label htmlFor="upload">
                    <span className="header-thick">{t("Upload Files")}</span>
                  </label>
                  <Upload
                    files={files}
                    setFiles={setFiles}
                    accept="image/jpg, image/jpeg, image/png"
                    variant="button"
                  />
                </div>
              )}

              <div className="form-group">
                <div className="header-thick">{t("Remarks")}</div>
                <textarea
                  className="form-control"
                  rows="5"
                  style={{ resize: "none" }}
                  placeholder={t("Remarks")}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>

              <div className="edit-button-wrapper">
                <button
                  onClick={handleSubmit}
                  className="btn btn-design btn-brand"
                >
                  {t("Submit")}
                </button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LeaveForm;
